import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { listBlogPosts } from "../services/blog";
import { ArrowRight, ArrowLeft, Calendar, Clock } from "lucide-react";

// Default posts shown only when the admin has not added any post yet.
const DEFAULT_POSTS = [
  {
    slug: "180g-pres-ned-ir",
    cover_url: "https://images.unsplash.com/photo-1601066525716-3cca33c6d4c6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    eyebrow: { az: "BƏLƏDÇİ", en: "GUIDE", ru: "ГИД" },
    title:   { az: "180g pres nədir və niyə vacibdir?", en: "What is a 180g pressing and why does it matter?", ru: "Что такое 180g пресс и почему это важно?" },
    excerpt: { az: "Audiofil presləmənin texniki tərəflərinə qısa baxış: çəki, materialın saflığı və səs dərinliyi arasında əlaqə.", en: "A quick look at audiophile pressings: weight, material purity, and sound depth.", ru: "Короткий обзор аудиофильных прессов: вес, чистота материала и глубина звука." },
    body:    { az: "", en: "", ru: "" },
    date: "2025-06-12", read_min: 4, published: true,
  },
  {
    slug: "baki-yerli-caz-sehnesi",
    cover_url: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    eyebrow: { az: "YERLİ SƏHNƏ", en: "LOCAL SCENE", ru: "МЕСТНАЯ СЦЕНА" },
    title:   { az: "Bakının yerli caz səhnəsindən beş ad", en: "Five names from Baku's local jazz scene", ru: "Пять имён местной джаз-сцены Баку" },
    excerpt: { az: "Şəhərin yeni nəsil caz musiqiçiləri və onların vinildə təqdim olunan ən yaxşı buraxılışları.", en: "New-generation jazz musicians from the city and their best vinyl releases.", ru: "Джаз-музыканты нового поколения и их лучшие винил-релизы." },
    body:    { az: "", en: "", ru: "" },
    date: "2025-05-28", read_min: 6, published: true,
  },
  {
    slug: "yeni-gelenler-iyul",
    cover_url: "https://images.unsplash.com/photo-1588532218970-c2cab983746a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    eyebrow: { az: "YENİ GƏLƏNLƏR", en: "NEW ARRIVALS", ru: "НОВИНКИ" },
    title:   { az: "İyul: arxivə daxil olan ən maraqlı 12 başlıq", en: "July: 12 most interesting titles added to the archive", ru: "Июль: 12 самых интересных пополнений архива" },
    excerpt: { az: "Bu ay Almaniya və Yaponiya preslərindən gələn nadir buraxılışlar — hamısı limitli sayda.", en: "Rare pressings from Germany and Japan this month — all limited.", ru: "В этом месяце редкие прессы из Германии и Японии — все лимитированные." },
    body:    { az: "", en: "", ru: "" },
    date: "2025-07-02", read_min: 3, published: true,
  },
];

// Multi-lingual picker that tolerates strings (legacy) and missing langs.
function pick(value, lang) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.en || value.az || Object.values(value)[0] || "";
}

export default function BlogPage() {
  const { t, tp, lang } = useApp();
  const tblog = t.blog || {};
  const { slug } = useParams();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listBlogPosts({ onlyPublished: true }).then((items) => {
      setPosts(items && items.length ? items : DEFAULT_POSTS);
      setLoading(false);
    }).catch(() => {
      setPosts(DEFAULT_POSTS);
      setLoading(false);
    });
  }, []);

  // Single-post detail view (/blog/:slug)
  if (slug) {
    const post = posts.find((p) => p.slug === slug || p.id === slug);
    if (loading) {
      return <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24 text-[var(--text-2)]">Yüklənir…</div>;
    }
    if (!post) {
      return (
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24" data-testid="blog-not-found">
          <div className="metadata text-[var(--text-muted)] mb-4">404 · YAZI</div>
          <h1 className="section-title text-4xl md:text-5xl">Yazı tapılmadı</h1>
          <Link to="/blog" className="btn-outline mt-8 inline-flex" style={{ textTransform: "none", letterSpacing: "0" }}>
            <ArrowLeft size={14}/> Bütün yazılar
          </Link>
        </div>
      );
    }

    const title   = pick(post.title, lang);
    const eyebrow = pick(post.eyebrow, lang);
    const excerpt = pick(post.excerpt, lang);
    const body    = pick(post.body, lang);

    return (
      <article className="bg-[var(--bg)]" data-testid={`blog-post-${slug}`}>
        <section className="max-w-screen-md mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
          <Link to="/blog" className="metadata draw-underline inline-flex items-center gap-2 text-[var(--text-2)] hover:text-[var(--primary)] mb-8">
            <ArrowLeft size={12}/> Bütün yazılar
          </Link>
          <div className="metadata text-[var(--text-muted)] mb-5 flex items-center gap-3 flex-wrap">
            {eyebrow && <span>{eyebrow}</span>}
            {post.date && <><span>·</span><span className="inline-flex items-center gap-1"><Calendar size={11}/> {new Date(post.date).toLocaleDateString()}</span></>}
            {post.read_min && <><span>·</span><span className="inline-flex items-center gap-1"><Clock size={11}/> {post.read_min} {tblog.minRead || "dəq oxu"}</span></>}
          </div>
          <h1 className="section-title text-4xl md:text-6xl leading-[1.05]">{title}</h1>
          {excerpt && <p className="text-[var(--text-2)] text-lg md:text-xl leading-relaxed mt-6">{excerpt}</p>}
        </section>

        {post.cover_url && (
          <section className="max-w-screen-xl mx-auto px-6 md:px-12 mb-12">
            <div className="aspect-[16/9] overflow-hidden">
              <img src={post.cover_url} alt={title} className="w-full h-full object-cover" />
            </div>
          </section>
        )}

        <section className="max-w-screen-md mx-auto px-6 md:px-12 pb-24">
          {body
            ? body.split(/\n+/).filter(Boolean).map((para, i) => (
                <p key={i} className="text-[var(--text)] text-base md:text-lg leading-[1.75] mt-5 first:mt-0">{para}</p>
              ))
            : <p className="text-[var(--text-muted)] italic">Bu yazının tam mətni hələ əlavə olunmayıb.</p>}
        </section>
      </article>
    );
  }

  // ── List view ──────────────────────────────────────────────
  const visible = posts;
  const featured = visible[0];
  const rest = visible.slice(1);

  return (
    <div className="bg-[var(--bg)]" data-testid="blog-page">
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12 border-b border-[var(--border-soft)]">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="metadata text-[var(--text-muted)] mb-4">{tp("blog", "eyebrow", tblog.eyebrow || "JURNAL · YENİLİKLƏR")}</div>
            <h1 className="section-title text-5xl md:text-7xl">{tp("blog", "title", tblog.title || "Yeniliklər")}</h1>
          </div>
          <p className="text-[var(--text-2)] max-w-md text-base md:text-lg leading-relaxed">
            {tp("blog", "lede", tblog.lede || "Vinil səhnəsindən qısa qeydlər, bələdçilər və mağazaya yeni daxil olan buraxılışlar.")}
          </p>
        </div>
      </section>

      {loading ? (
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24 text-[var(--text-2)]">Yüklənir…</div>
      ) : visible.length === 0 ? (
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24 text-center text-[var(--text-muted)]">
          Hələ yazı yoxdur.
        </div>
      ) : (
        <>
          {featured && (
            <section className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16">
              <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-12 gap-10 items-end" data-testid="blog-featured">
                <div className="md:col-span-7 zoom-wrap aspect-[16/10] overflow-hidden">
                  {featured.cover_url
                    ? <img src={featured.cover_url} alt={pick(featured.title, lang)} className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-[var(--surface)]" />}
                </div>
                <div className="md:col-span-5">
                  <div className="flex items-center gap-3 metadata text-[var(--text-muted)] flex-wrap">
                    {pick(featured.eyebrow, lang) && <span>{pick(featured.eyebrow, lang)}</span>}
                    {featured.date && <><span>·</span><span>{new Date(featured.date).toLocaleDateString()}</span></>}
                    {featured.read_min && <><span>·</span><span>{featured.read_min} {tblog.minRead || "dəq oxu"}</span></>}
                  </div>
                  <h2 className="section-title text-3xl md:text-5xl mt-4 group-hover:text-[var(--primary)] transition-colors">
                    {pick(featured.title, lang)}
                  </h2>
                  <p className="text-[var(--text-2)] text-base md:text-lg leading-relaxed mt-5">{pick(featured.excerpt, lang)}</p>
                  <span className="metadata draw-underline mt-6 inline-flex items-center gap-2 group-hover:text-[var(--primary)]">
                    {tblog.readMore || "Oxu"} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </section>
          )}

          {rest.length > 0 && (
            <section className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-24 border-t border-[var(--border-soft)] pt-16">
              <div className="grid md:grid-cols-3 gap-x-10 gap-y-16">
                {rest.map((p) => (
                  <Link key={p.id || p.slug} to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
                    <div className="zoom-wrap aspect-[4/3] overflow-hidden mb-5">
                      {p.cover_url
                        ? <img src={p.cover_url} alt={pick(p.title, lang)} className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-[var(--surface)]" />}
                    </div>
                    <div className="metadata text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
                      {pick(p.eyebrow, lang) && <span>{pick(p.eyebrow, lang)}</span>}
                      {p.date && <><span>·</span><span>{new Date(p.date).toLocaleDateString()}</span></>}
                    </div>
                    <h3 className="section-title text-xl md:text-2xl mt-3 group-hover:text-[var(--primary)] transition-colors">{pick(p.title, lang)}</h3>
                    <p className="text-[var(--text-2)] text-sm md:text-base leading-relaxed mt-2 line-clamp-3">{pick(p.excerpt, lang)}</p>
                    <span className="metadata draw-underline mt-4 inline-flex items-center gap-2 group-hover:text-[var(--primary)]">
                      {tblog.readMore || "Oxu"} <ArrowRight size={12} />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
